import { CustomSelect } from '@uikit/Tables/Filtration/selects/CustomSelect';
import CustomInput from '@uikit/Tables/Filtration/selects/CustomInput';
import { CloseIcon, PlusIcon } from '@src/svg';
import { Dispatch, FC, SetStateAction } from 'react';
import {
  FilterErrorIndicator,
  filterOptionType,
  OperatorInfo,
} from '@uikit/Tables/Filtration/constants';
import { idType, newInfoType, selectItemType } from '@src/types';

type InputValueTypes =
  | string
  | number
  | null
  | Date
  | Date[]
  | number[]
  | { id: idType; value: boolean; label: string };

type FilterFieldProps = {
  className?: string;
  dataTestId?: string;
  filterDraftResetKey: number;
  selectedColumn: filterOptionType | null;
  setSelectedColumn: (option: filterOptionType | null) => void;
  fieldColumns?: filterOptionType[];
  tableInfo?: newInfoType;
  selectedOperator: selectItemType | null;
  setSelectedOperator?: (option: selectItemType | null) => void;
  groupFilter: OperatorInfo | null;
  errorIndicator?: FilterErrorIndicator;
  setErrorIndicator: Dispatch<SetStateAction<FilterErrorIndicator>>;
  inputValue: InputValueTypes;
  setInputValue: (value: InputValueTypes) => void;
  onSubmit?: (e: any) => void;
  isSubmitDisabled?: boolean;
  handleClose?: () => void;
};

export const FilterPopup: FC<FilterFieldProps> = ({
  dataTestId,
  className,
  filterDraftResetKey,
  selectedColumn,
  setSelectedColumn,
  fieldColumns,
  tableInfo,
  setSelectedOperator,
  selectedOperator,
  groupFilter,
  errorIndicator,
  setErrorIndicator,
  handleClose,
  onSubmit,
  isSubmitDisabled = false,
  setInputValue,
  inputValue,
}) => {
  const activeGroupFilter = selectedColumn ? groupFilter : null;

  const allFieldsApplied =
    !!fieldColumns?.length && fieldColumns.every((column) => column.disabled);

  return (
    <div className={className} data-test-id="filter-field-popup">
      <div data-test-id="filter-field-popup-content">
        <div className="select-field-containers">
          <div className="fill-fields-row">
            <div className="filter-row">
              <CustomSelect
                key={`columns-${filterDraftResetKey}`}
                selectedOption={selectedColumn}
                setSelectedOption={setSelectedColumn}
                placeholder="Fields"
                selectOptions={fieldColumns}
                disabled={allFieldsApplied}
                info={{ toggle: true, desc: tableInfo?.desc }}
                warningIndicator={errorIndicator}
                warningKey="field"
                dataTestId={`${dataTestId}-field-select-columns`}
                setInputValue={setInputValue}
              />
              <CustomSelect
                key={`operator-${filterDraftResetKey}-${selectedColumn?.filterKey || 'empty'}`}
                placeholder="Operator"
                selectedOption={selectedOperator}
                setSelectedOption={setSelectedOperator}
                type={activeGroupFilter?.type}
                selectOptions={activeGroupFilter?.operators}
                warningIndicator={errorIndicator}
                warningKey="operator"
                disabled={
                  !selectedColumn || activeGroupFilter?.operators?.length === 1
                }
                dataTestId={`${dataTestId}-field-select-operator`}
              />
              <CustomInput
                groupFilter={activeGroupFilter}
                requestByAsync={selectedColumn?.requestByAsync}
                selectsRange={
                  !!(selectedOperator && selectedOperator.value === 'range')
                }
                setErrorIndicator={setErrorIndicator}
                name={selectedColumn?.name}
                type={activeGroupFilter?.type}
                errorIndicator={errorIndicator}
                inputValue={inputValue}
                setInputValue={setInputValue}
                placeholder="Value"
                disabled={
                  groupFilter?.type === 'Boolean' ||
                  !selectedColumn ||
                  activeGroupFilter?.type === 'Boolean'
                }
                dataTestId={`${dataTestId}-field-input`}
              />
            </div>
          </div>
          <div className="filter-button_wrapper">
            <button
              type="button"
              onClick={onSubmit}
              disabled={isSubmitDisabled}
              aria-disabled={isSubmitDisabled}
              data-test-id={`${dataTestId}-submit-button`}
            >
              <PlusIcon color="#FFFFFF" />
            </button>
            <button
              onClick={handleClose}
              className="close-button"
              data-test-id={`${dataTestId}-close-button`}
            >
              <CloseIcon width={24} height={24} color="#FFFFFF" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
